import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { Button } from './ui/button';
import { MessageCircle, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * GalleryLightbox Component
 * 
 * Modern lightbox gallery with conversion-focused features:
 * - Full-screen image viewing with zoom
 * - Thumbnail navigation
 * - Technical captions
 * - Integrated CTA buttons (WhatsApp & Quote)
 * - Keyboard & touch navigation
 * 
 * @param {Array} images - Array of image objects with { src, caption, title }
 * @param {Function} onQuoteClick - Handler for quote button
 * @param {string} whatsappMessage - Pre-filled WhatsApp message
 */
const GalleryLightbox = ({ images, onQuoteClick, whatsappMessage }) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const { language } = useLanguage();

  const translations = {
    en: {
      requestQuote: 'Request Quote for Similar Project',
      whatsapp: 'WhatsApp'
    },
    es: {
      requestQuote: 'Cotizar proyecto similar',
      whatsapp: 'WhatsApp'
    }
  };

  const t = translations[language];

  // Prepare slides for lightbox with captions
  const slides = images.map(img => ({
    src: img.src,
    title: img.title,
    description: img.caption
  }));

  return (
    <>
      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((image, idx) => (
          <div 
            key={idx}
            onClick={() => {
              setIndex(idx);
              setOpen(true);
            }}
            className="relative overflow-hidden rounded-lg border border-gray-700 group aspect-square cursor-pointer"
          >
            <img 
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            {/* Hover overlay with info */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
              <h3 className="text-white font-semibold text-lg mb-2">{image.title}</h3>
              <p className="text-gray-300 text-sm">{image.caption}</p>
              <div className="mt-4 flex items-center gap-2 text-[#B91C1C] text-sm font-semibold">
                <span>🔍</span>
                <span>{language === 'en' ? 'Click to view details' : 'Click para ver detalles'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
        plugins={[Captions, Thumbnails, Zoom, Fullscreen]}
        captions={{
          showToggle: true,
          descriptionTextAlign: 'center'
        }}
        thumbnails={{
          position: 'bottom',
          width: 120,
          height: 80,
          border: 2,
          borderRadius: 4,
          padding: 4,
          gap: 16
        }}
        zoom={{
          maxZoomPixelRatio: 3,
          scrollToZoom: true
        }}
        animation={{
          fade: 300,
          swipe: 300
        }}
        controller={{
          closeOnBackdropClick: true
        }}
        on={{
          view: ({ index: currentIndex }) => setIndex(currentIndex)
        }}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
          captionsTitle: { 
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#B91C1C',
            marginBottom: '0.5rem'
          },
          captionsDescription: {
            fontSize: '1rem',
            color: '#D1D5DB'
          }
        }}
        render={{
          buttonPrev: images.length <= 1 ? () => null : undefined,
          buttonNext: images.length <= 1 ? () => null : undefined,
          iconPrev: () => (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          iconNext: () => (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          // Custom slide with CTA buttons overlay
          slide: ({ slide }) => (
            <div className="relative w-full h-full flex items-center justify-center">
              <img 
                src={slide.src} 
                alt={slide.title}
                style={{ 
                  maxWidth: '90vw', 
                  maxHeight: '90vh', 
                  objectFit: 'contain' 
                }}
              />
              {/* CTA Buttons Overlay - Bottom Center */}
              <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex flex-col sm:flex-row gap-3 z-50">
                <Button 
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onQuoteClick();
                  }}
                  className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-6 py-3 text-base font-semibold shadow-xl shadow-[#B91C1C]/40 backdrop-blur-sm"
                >
                  <FileText className="mr-2" size={18} />
                  {t.requestQuote}
                </Button>
                <Button 
                  size="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`https://wa.me/526181719959?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
                  }}
                  className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-3 text-base font-semibold shadow-xl shadow-[#25D366]/40 backdrop-blur-sm"
                >
                  <MessageCircle className="mr-2" size={18} />
                  {t.whatsapp}
                </Button>
              </div>
            </div>
          )
        }}
      />
    </>
  );
};

export default GalleryLightbox;
